import {
  Mail,
  MapPin,
  MessageCircle,
  PackageCheck,
  Phone,
  X
} from "lucide-react";

import type {
  AdminOrder
} from "../../../types/admin";

interface Props {
  order: AdminOrder;
  onClose: () => void;
}

function money(
  value: string
) {
  return Number(
    value
  ).toLocaleString(
    "en-IN"
  );
}

function label(
  value: string
) {
  return value
    .toLowerCase()
    .replace(
      /_/g,
      " "
    )
    .replace(
      /\b\w/g,
      (character) =>
        character.toUpperCase()
    );
}

export function AdminOrderDetailsModal({
  order,
  onClose
}: Props) {
  const whatsappNumber =
    (
      order.whatsappNumber ||
      order.phone
    ).replace(
      /\D/g,
      ""
    );

  const message = [
    `Hello ${order.customerName},`,
    "",
    `Regarding your RiNi Bodycare order ${order.orderNumber}.`,
    "",
    `Order total: ₹${money(order.totalAmount)}`,
    "",
    "Thank you for choosing RiNi Bodycare."
  ].join("\n");

  const whatsappUrl =
    `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
      message
    )}`;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-[2rem] bg-[#f7f4ef] shadow-2xl">
        <div className="sticky top-0 z-10 flex items-start justify-between border-b border-[#5e3b2c]/10 bg-[#f7f4ef] px-6 py-5 sm:px-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#70845b]">
              Order details
            </p>

            <h2 className="mt-2 font-serif text-3xl text-[#38251e]">
              {order.orderNumber}
            </h2>

            <p className="mt-2 text-sm text-[#746d65]">
              {new Date(
                order.createdAt
              ).toLocaleString(
                "en-IN"
              )}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-white p-3 text-[#5e3b2c] shadow-sm"
            aria-label="Close order details"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-6 p-6 sm:p-8">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-2xl bg-white p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#70845b]">
                Order status
              </p>

              <p className="mt-3 text-lg font-semibold text-[#38251e]">
                {label(
                  order.orderStatus
                )}
              </p>
            </div>

            <div className="rounded-2xl bg-white p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#70845b]">
                Payment
              </p>

              <p className="mt-3 text-lg font-semibold text-[#38251e]">
                {label(
                  order.paymentStatus
                )}
              </p>

              <p className="mt-1 text-xs text-[#746d65]">
                Manual payment
              </p>
            </div>

            <div className="rounded-2xl bg-[#efe3d2] p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#70845b]">
                Order total
              </p>

              <p className="mt-3 text-2xl font-semibold text-[#5e3b2c]">
                ₹
                {money(
                  order.totalAmount
                )}
              </p>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <section className="rounded-2xl bg-white p-6">
              <h3 className="font-serif text-2xl text-[#38251e]">
                Customer
              </h3>

              <p className="mt-5 text-lg font-semibold text-[#38251e]">
                {order.customerName}
              </p>

              <div className="mt-4 space-y-3 text-sm text-[#746d65]">
                <div className="flex items-center gap-3">
                  <Phone
                    size={17}
                    className="text-[#70845b]"
                  />

                  {order.phone}
                </div>

                {order.email && (
                  <div className="flex items-center gap-3">
                    <Mail
                      size={17}
                      className="text-[#70845b]"
                    />

                    <span className="break-all">
                      {order.email}
                    </span>
                  </div>
                )}
              </div>

              <a
                href={whatsappUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#edf1e8] px-4 py-3 text-sm font-semibold text-[#526746]"
              >
                <MessageCircle
                  size={17}
                />

                WhatsApp customer
              </a>
            </section>

            <section className="rounded-2xl bg-white p-6">
              <div className="flex items-center gap-3">
                <MapPin
                  size={20}
                  className="text-[#70845b]"
                />

                <h3 className="font-serif text-2xl text-[#38251e]">
                  Delivery address
                </h3>
              </div>

              <p className="mt-5 leading-7 text-[#746d65]">
                {order.addressLine1}
                <br />

                {order.addressLine2 && (
                  <>
                    {order.addressLine2}
                    <br />
                  </>
                )}

                {order.city},
                {" "}
                {order.state}
                <br />

                PIN:
                {" "}
                {order.postalCode}
              </p>
            </section>
          </div>

          <section className="rounded-2xl bg-white p-6">
            <div className="flex items-center gap-3">
              <PackageCheck
                size={20}
                className="text-[#70845b]"
              />

              <h3 className="font-serif text-2xl text-[#38251e]">
                Products
              </h3>
            </div>

            <div className="mt-6 overflow-x-auto">
              <table className="w-full min-w-[650px] text-left">
                <thead>
                  <tr className="border-b border-[#5e3b2c]/10 text-xs uppercase tracking-[0.1em] text-[#70845b]">
                    <th className="pb-3">
                      Product
                    </th>

                    <th className="pb-3">
                      SKU
                    </th>

                    <th className="pb-3 text-center">
                      Qty
                    </th>

                    <th className="pb-3 text-right">
                      Unit price
                    </th>

                    <th className="pb-3 text-right">
                      Total
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {order.items.map(
                    (item) => (
                      <tr
                        key={item.id}
                        className="border-b border-[#5e3b2c]/5"
                      >
                        <td className="py-4 font-medium text-[#38251e]">
                          {item.productName}
                        </td>

                        <td className="py-4 text-sm text-[#746d65]">
                          {item.sku}
                        </td>

                        <td className="py-4 text-center text-sm">
                          {item.quantity}
                        </td>

                        <td className="py-4 text-right text-sm">
                          ₹
                          {money(
                            item.unitPrice
                          )}
                        </td>

                        <td className="py-4 text-right font-semibold text-[#38251e]">
                          ₹
                          {money(
                            item.lineTotal
                          )}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="grid gap-6 lg:grid-cols-[1fr_360px]">
            <div>
              {order.notes && (
                <div className="rounded-2xl bg-[#edf1e8] p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#70845b]">
                    Customer notes
                  </p>

                  <p className="mt-3 leading-7 text-[#746d65]">
                    {order.notes}
                  </p>
                </div>
              )}
            </div>

            <div className="rounded-2xl bg-[#efe3d2] p-6">
              <div className="flex justify-between">
                <span className="text-[#746d65]">
                  Subtotal
                </span>

                <span className="font-semibold">
                  ₹
                  {money(
                    order.subtotal
                  )}
                </span>
              </div>

              <div className="mt-3 flex justify-between">
                <span className="text-[#746d65]">
                  Shipping
                </span>

                <span className="font-semibold">
                  ₹
                  {money(
                    order.shippingAmount
                  )}
                </span>
              </div>

              <div className="mt-5 flex justify-between border-t border-[#5e3b2c]/10 pt-5">
                <span className="font-serif text-xl text-[#38251e]">
                  Total
                </span>

                <span className="text-xl font-semibold text-[#5e3b2c]">
                  ₹
                  {money(
                    order.totalAmount
                  )}
                </span>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
